import useSWR, { useSWRConfig, type SWRResponse } from "swr";
import type { AuthContextType } from "#context/AuthContext.tsx";

type ProfileAppearance = {
  profileAvatar?: AuthContextType["avatar"];
  profileAvatarBorder?: AuthContextType["border"];
  customCursor?: AuthContextType["cursor"];
};

export type UserProfileResponse = Partial<AuthContextType> & {
  username: string;
  /**
   * What the move to the Robinhood Chain parked out of the balance, waiting to
   * be claimed back as $PLAYR — the blocked part of it in its own column, same
   * as on the live side. Named after the columns themselves, and reported here
   * rather than on the balance — that endpoint answers what a player can spend
   * — so the claim can be offered from a read the app already makes.
   *
   * Optional: absent means "this deploy does not say", not "nothing parked".
   */
  tk_balance_tico?: string;
  tk_balance_withdrawable_blocked_tico?: string;
  /**
   * Spins left to play on Playr Bar — `users.spins_balance`, the column the
   * Playr Bar backend spends from.
   *
   * Optional: absent means "this deploy does not say", not "no spins".
   */
  spins_balance?: number;
  appearance?: ProfileAppearance | null;
  has_customized_username: boolean;
  requires_initial_username_change: boolean;
  username_changed_at: string | null;
  username_change_available_at: string | null;
  registration_origin: "oidc" | "password" | "code" | "passkey" | "legacy";
  /**
   * Whether to offer the one-time "Protect Your Progress!" backup login: the
   * account has no email and was registered with a method that never gives
   * one (passkey, Telegram, EVM or TON wallet), and the offer is unanswered.
   *
   * Optional: absent means "this deploy does not say" — a profile restored
   * from storage may predate the field — and is read as "do not offer".
   */
  requires_backup_login_prompt?: boolean;
};

/** The profile of the signed-in user, `null` when there is no session. */
export type UserProfile = UserProfileResponse | null;

/**
 * The cache entry every profile reader shares. It is not the bare endpoint
 * path: `useRankAndSeason` keys its own request on `"user/profile"` and stores
 * a different shape under it.
 */
export const PROFILE_KEY = ["user/profile", "current"] as const;

/**
 * Reads the shared profile, `undefined` while it is still unknown. Passing no
 * fetcher makes this a pure subscriber: it re-renders on cache changes and
 * never issues a request of its own — `useUserData` owns the loading.
 */
export function useProfile(): SWRResponse<UserProfile, unknown, unknown> {
  return useSWR<UserProfile>(PROFILE_KEY, null);
}

/** Forgets the cached profile — the sign-out counterpart of a load. */
export function useResetProfile(): () => Promise<unknown> {
  const { mutate } = useSWRConfig();
  return () => mutate(PROFILE_KEY, null, { revalidate: false });
}
